import { StaticImageData } from "next/image";

import {
  CATALOG_HOME_LINK_TYPE,
  type LinkWithLangType,
} from "@stadium/shared/src/constants/stores/routes";
import type { LinkRouteDefaults } from "@stadium/shared/src/components/link-with-lang";
import { LinkWithLang } from "components/plasmic/shared/link-with-lang/client";
import ResponsiveImage from "@stadium/shared/src/components/shared/responsive-image";

import styles from "./main.module.scss";

export interface ImageWithHeight {
  image?: string | StaticImageData;
  height?: string;
}

export interface H01Props {
  eyebrowText?: string;
  heading?: string;
  description?: string;
  ctaText?: string;
  ctaLinkType?: string;
  openInNewTab?: boolean;
  otherUrl?: string;
  categorySlug?: string;

  cta2Text?: string;
  cta2LinkType?: string;
  cta2OpenInNewTab?: boolean;
  cta2OtherUrl?: string;
  cta2CategorySlug?: string;

  mobileImg?: string | StaticImageData;
  tabletImg?: ImageWithHeight;
  desktopImg?: ImageWithHeight;
  highResImg?: ImageWithHeight;
  imageAlt?: string;

  autoplayDelay?: number;
  className?: string;
  standardMargin?: boolean;
  linkRouteDefaults?: LinkRouteDefaults;
}

export default function H01({
  eyebrowText = "SWAG THAT SPARKS CONNECTION",
  heading = "Welcome to the Center of Swag",
  description = "Let your swag speak volumes with premium products from the world's greatest catalog.",
  ctaText = "Talk to sales",
  mobileImg = "https://cdn.bystadium.com/plasmic/static-slices/hero-mobile-img.png",
  tabletImg = {
    image: "https://cdn.bystadium.com/plasmic/static-slices/hero-mobile-img.png",
    height: "27.125rem",
  },
  desktopImg = {
    image: "https://cdn.bystadium.com/plasmic/static-slices/hero-desktop-img.png",
    height: "32rem",
  },
  highResImg = {
    image: "https://cdn.bystadium.com/plasmic/static-slices/hero-desktop-img.png",
    height: "40rem",
  },
  ctaLinkType = CATALOG_HOME_LINK_TYPE,
  openInNewTab = false,
  otherUrl = "",
  categorySlug = "",
  cta2Text,
  cta2LinkType = CATALOG_HOME_LINK_TYPE,
  cta2OpenInNewTab = false,
  cta2OtherUrl = "",
  cta2CategorySlug = "",
  imageAlt = "Person holding colorful swag t-shirt",
  standardMargin = false,
  className,
  linkRouteDefaults,
}: Readonly<H01Props>) {
  return (
    <section
      className={`${styles.sectionWrapper} ${
        standardMargin ? styles.standardMargin : styles.h01Margin
      } ${className || ""}`}
    >
      <div
        className={styles.imageContainer}
        style={
          {
            "--tablet-height": tabletImg.height,
            "--desktop-height": desktopImg.height,
            "--highres-height": highResImg.height,
          } as React.CSSProperties
        }
      >
        <ResponsiveImage
          mobileSrc={mobileImg}
          tabletSrc={tabletImg.image}
          desktopSrc={highResImg.image || desktopImg.image}
          alt={imageAlt}
          className={styles.image}
          priority
          fetchPriority="high"
        />
      </div>

      <div
        className={styles.contentWrapper}
        style={
          {
            "--desktop-height": desktopImg.height,
            "--highres-height": highResImg.height,
          } as React.CSSProperties
        }
      >
        <div className={styles.contentInner}>
          <div className={styles.textContent}>
            <p className={styles.eyebrow}>{eyebrowText}</p>
            <h1 className={styles.heading}>{heading}</h1>
            <p className={styles.description}>{description}</p>
          </div>
          {(ctaText || cta2Text) && (
            <div className={styles.ctaWrapper}>
              {ctaText && (
                <LinkWithLang
                  {...linkRouteDefaults}
                  type={ctaLinkType as LinkWithLangType}
                  data={{ code: categorySlug, url: otherUrl }}
                  target={openInNewTab ? "_blank" : "_self"}
                  showExternalLinkIcon={openInNewTab}
                  className={styles.primaryBtn}
                >
                  {ctaText}
                </LinkWithLang>
              )}
              {cta2Text && (
                <LinkWithLang
                  {...linkRouteDefaults}
                  type={cta2LinkType as LinkWithLangType}
                  data={{ code: cta2CategorySlug, url: cta2OtherUrl }}
                  target={cta2OpenInNewTab ? "_blank" : "_self"}
                  showExternalLinkIcon={cta2OpenInNewTab}
                  className={styles.secondaryBtn}
                >
                  {cta2Text}
                </LinkWithLang>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
